import {
  typedCollection,
  typedDoc,
  getCollection,
  addDoc,
  setDocument,
  updateDocument,
  deleteDocument,
  where,
  orderBy,
  serverTimestamp,
} from '@/lib/firebase/firestore'
import { submitTestimonialFn } from '@/lib/firebase/functions'
import type { TestimonialDocument } from '@/types'

const col = () => typedCollection<TestimonialDocument>('testimonials')
const docRef = (id: string) => typedDoc<TestimonialDocument>('testimonials', id)

export type TestimonialWithId = TestimonialDocument & { id: string }

export interface SubmitSurveyPayload {
  engagementId: string
  clientUserId: string
  clientName: string
  clientEmail: string
  clientTitle?: string
  serviceType?: string
  rating: number
  content: string
}

export interface AdminCreatePayload {
  clientName: string
  clientEmail: string
  clientTitle?: string
  serviceType?: string
  rating: number
  content: string
}

// Minimal type for survey email request documents
interface SurveyEmailRequest {
  engagementId: string
  requestedBy: string
  status: 'pending'
  createdAt: ReturnType<typeof serverTimestamp>
}

export const testimonialService = {
  /** Submit a survey review via the rate-limited callable. */
  async submitSurvey(data: SubmitSurveyPayload): Promise<string> {
    const res = await submitTestimonialFn({
      engagementId: data.engagementId,
      clientUserId: data.clientUserId,
      clientName: data.clientName,
      clientEmail: data.clientEmail,
      ...(data.clientTitle ? { clientTitle: data.clientTitle } : {}),
      serviceType: data.serviceType,
      rating: data.rating,
      content: data.content,
    })
    return res.data.testimonialId
  },

  /** Write a survey email request; the Firestore trigger handles the actual send. */
  async requestSurveyEmail(engagementId: string, requestedBy: string): Promise<void> {
    const request: SurveyEmailRequest = {
      engagementId,
      requestedBy,
      status: 'pending',
      createdAt: serverTimestamp(),
    }
    await addDoc(typedCollection<SurveyEmailRequest>('surveyEmailRequests'), request)
  },

  /** Fetch all approved testimonials sorted by display order. */
  async getApproved(): Promise<TestimonialWithId[]> {
    return getCollection(col(), where('status', '==', 'approved'), orderBy('order', 'asc'))
  },

  /** Fetch all testimonials for admin use, sorted newest first. */
  async getAll(): Promise<TestimonialWithId[]> {
    return getCollection(col(), orderBy('createdAt', 'desc'))
  },

  /** Check whether a survey has already been submitted for a given engagement + user. */
  async getByEngagementAndUser(
    engagementId: string,
    userId: string
  ): Promise<TestimonialWithId | null> {
    const results = await getCollection(
      col(),
      where('engagementId', '==', engagementId),
      where('clientUserId', '==', userId)
    )
    return results[0] ?? null
  },

  /**
   * Create a testimonial manually (admin-only).
   * Firestore rules allow this write only for admins.
   */
  async adminCreateManual(data: AdminCreatePayload): Promise<string> {
    const id = crypto.randomUUID()
    const ref = docRef(id)
    await setDocument(ref, {
      id,
      clientUserId: '',
      clientName: data.clientName,
      clientEmail: data.clientEmail,
      ...(data.clientTitle ? { clientTitle: data.clientTitle } : {}),
      ...(data.serviceType ? { serviceType: data.serviceType } : {}),
      rating: data.rating,
      content: data.content,
      status: 'pending',
      source: 'manual',
      order: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    } as unknown as TestimonialDocument)
    return id
  },

  async updatePhotoUrl(id: string, photoUrl: string): Promise<void> {
    await updateDocument(docRef(id), {
      photoUrl,
      updatedAt: serverTimestamp() as unknown as Date,
    })
  },

  async updateOrder(id: string, order: number): Promise<void> {
    await updateDocument(docRef(id), {
      order,
      updatedAt: serverTimestamp() as unknown as Date,
    })
  },

  async approve(id: string): Promise<void> {
    await updateDocument(docRef(id), {
      status: 'approved',
      approvedAt: serverTimestamp() as unknown as import('firebase/firestore').Timestamp,
      updatedAt: serverTimestamp() as unknown as Date,
    })
  },

  async reject(id: string): Promise<void> {
    await updateDocument(docRef(id), {
      status: 'rejected',
      updatedAt: serverTimestamp() as unknown as Date,
    })
  },

  async deleteTestimonial(id: string): Promise<void> {
    await deleteDocument(docRef(id))
  },
}
